/*
 * NSE Phase 1 search preview (2026-10-29): "Find my machine" quiz logic.
 * Rules-based scoring over the machine list printed by sections/nse-find-my-machine.liquid.
 * - Process is the main filter; material, format and budget add to the score.
 * - Quote-only machines (no price data in the list) only ever get "Request a quote".
 * - Buyable machines get quick-add via /cart/add.js. A second guard refuses to add anything
 *   flagged as quote-only.
 */
if (!customElements.get('nse-find-my-machine')) {
  customElements.define(
    'nse-find-my-machine',
    class NseFindMyMachine extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.form = this.querySelector('[data-nse-fmm-form]');
        this.results = this.querySelector('[data-nse-fmm-results]');
        var dataEl = this.querySelector('[data-nse-fmm-data]');
        try {
          this.machines = JSON.parse(dataEl ? dataEl.textContent : '[]').map(this.classify);
        } catch (e) {
          this.machines = [];
        }
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.show();
        });
        this.form.addEventListener('reset', () => {
          this.results.innerHTML = '';
        });
      }

      /* ---------- rules ---------- */
      classify(m) {
        var primary = [m.type, m.title, m.cats, m.mf].join(' ').toLowerCase();
        var tags = (m.tags || '').toLowerCase();
        var has = (text, re) => re.test(text);

        function processesFrom(text) {
          var p = new Set();
          if (has(text, /3-in-1/)) ['welding', 'cutting', 'cleaning'].forEach((x) => p.add(x));
          if (has(text, /weld/)) p.add('welding');
          if (has(text, /clean/)) p.add('cleaning');
          if (has(text, /press brake|press-brake|\bbend/)) p.add('bending');
          if (has(text, /tube|pipe/)) {
            p.add('tube');
            p.add('cutting');
          }
          if (has(text, /engrav|marking|marker/)) p.add('engraving');
          if (has(text, /cutter|cutting/)) p.add('cutting');
          if (has(text, /printer|heat press|\bdtf\b|apparel/)) p.add('printing');
          return p;
        }

        var processes = processesFrom(primary);
        if (!processes.size) processes = processesFrom(tags); // fallback when type/title say nothing

        var laser = '';
        if (has(primary, /\buv\b/)) laser = 'uv';
        else if (has(primary, /\bco2\b/)) laser = 'co2';
        else if (has(primary, /fiber|mopa/)) laser = 'fiber';
        else if (has(primary, /diode/)) laser = 'diode';

        var desktop = has(primary, /desktop|heat press|wonderpress/);
        var materials = new Set();
        if (processes.has('printing')) materials.add('fabric');
        if (['welding', 'cleaning', 'bending', 'tube'].some((x) => processes.has(x)) || laser === 'fiber' || has(primary, /metalfab|metal/)) {
          materials.add('steel');
          materials.add('aluminum');
        }
        if (processes.has('cleaning')) materials.add('coatings');
        if (laser === 'co2') ['organic', 'fabric', 'glass'].forEach((x) => materials.add(x));
        if (laser === 'uv') ['glass', 'organic'].forEach((x) => materials.add(x));
        if (desktop && processes.has('engraving')) {
          materials.add('organic');
          // "Ultra" desktop models (non-UV) in this catalog add a fiber/IR source for metal marking.
          if (has(primary, /ultra/) && laser !== 'uv') {
            materials.add('steel');
            materials.add('aluminum');
          }
        }
        if (!materials.size && processes.has('cutting')) {
          materials.add('steel');
          materials.add('aluminum');
        }

        var format = 'floor';
        var onlyHandTools = processes.size > 0 && [...processes].every((x) => x === 'welding' || x === 'cleaning');
        if ((has(primary + ' ' + m.cats, /handheld|portable|lumx-handheld/) || onlyHandTools) && !has(primary, /table|tube|press brake/)) format = 'handheld';
        else if (desktop || processes.has('printing')) format = 'desktop';

        m.processes = processes;
        m.materials = materials;
        m.format = format;
        return m;
      }

      budgetMatch(m, band) {
        if (band === 'any') return true;
        if (m.quote) return band === 'b4';
        var ranges = { b1: [0, 500000], b2: [500000, 1500000], b3: [1500000, 5000000], b4: [5000000, Infinity] };
        var r = ranges[band];
        if (!r) return true;
        return m.min < r[1] && m.max >= r[0];
      }

      show() {
        var data = new FormData(this.form);
        var a = {
          process: data.get('process') || 'any',
          material: data.get('material') || 'any',
          format: data.get('format') || 'any',
          budget: data.get('budget') || 'any',
        };

        var scored = this.machines
          .filter((m) => a.process === 'any' || m.processes.has(a.process))
          .map((m) => {
            var hits = 0;
            var asked = 0;
            if (a.material !== 'any') {
              asked++;
              if (m.materials.has(a.material)) hits++;
            }
            if (a.format !== 'any') {
              asked++;
              if (m.format === a.format) hits++;
            }
            if (a.budget !== 'any') {
              asked++;
              if (this.budgetMatch(m, a.budget)) hits++;
            }
            return { m: m, hits: hits, exact: hits === asked };
          })
          .sort((x, y) => y.hits - x.hits || Number(y.m.available) - Number(x.m.available));

        var exact = scored.filter((s) => s.exact);
        var list = exact.length ? exact : scored.filter((s) => s.hits > 0);
        var isClose = !exact.length;
        list = list.slice(0, 12);

        this.results.innerHTML = '';
        var heading = document.createElement('h2');
        heading.className = 'h3 nse-fmm__results-title';
        if (!list.length) {
          heading.textContent = 'No close match yet';
          this.results.appendChild(heading);
          var p = document.createElement('p');
          p.innerHTML = 'Try "Not sure" on one of the questions, <a class="link" href="/search?q=laser&options%5Bunavailable_products%5D=show">browse all machines</a>, or <a class="link" href="/pages/contact">ask our team</a>.';
          this.results.appendChild(p);
        } else {
          heading.textContent = isClose
            ? 'Closest matches (' + list.length + ')'
            : list.length + (list.length === 1 ? ' machine fits' : ' machines fit');
          this.results.appendChild(heading);
          if (isClose) {
            var note = document.createElement('p');
            note.className = 'nse-fmm__note';
            note.textContent = 'Nothing fits every answer, so these meet the most of them.';
            this.results.appendChild(note);
          }
          var ul = document.createElement('ul');
          ul.className = 'nse-fmm__grid';
          ul.setAttribute('role', 'list');
          list.forEach((s) => ul.appendChild(this.card(s.m)));
          this.results.appendChild(ul);
        }
        this.results.focus();
        this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      /* ---------- cards ---------- */
      card(m) {
        var li = document.createElement('li');
        li.className = 'nse-fmm__card card-wrapper';
        li.dataset.productId = m.id;
        li.dataset.quote = m.quote ? 'true' : 'false';

        var media = document.createElement('a');
        media.href = m.url;
        media.className = 'nse-fmm__media';
        media.tabIndex = -1;
        media.setAttribute('aria-hidden', 'true');
        if (m.image) {
          var img = document.createElement('img');
          img.src = m.image;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 600;
          img.height = 600;
          media.appendChild(img);
        }
        li.appendChild(media);

        var body = document.createElement('div');
        body.className = 'nse-fmm__body';
        var brand = document.createElement('p');
        brand.className = 'caption-with-letter-spacing nse-fmm__brand';
        brand.textContent = m.brand;
        body.appendChild(brand);

        var h = document.createElement('h3');
        h.className = 'h5 nse-fmm__title';
        var link = document.createElement('a');
        link.href = m.url;
        link.className = 'full-unstyled-link';
        link.textContent = m.title;
        h.appendChild(link);
        body.appendChild(h);

        var price = document.createElement('p');
        price.className = 'nse-fmm__price price';
        body.appendChild(price);

        var actions = document.createElement('div');
        actions.className = 'nse-fmm__card-actions';
        body.appendChild(actions);
        li.appendChild(body);

        if (m.quote) {
          price.textContent = 'Request a quote';
          var q = document.createElement('a');
          q.className = 'button button--primary button--full-width';
          q.href = m.url + '#request-quote';
          q.textContent = 'Request a quote';
          q.setAttribute('aria-label', 'Request a quote: ' + m.title);
          actions.appendChild(q);
          return li;
        }

        var variants = (m.variants || []).filter((v) => v.available);
        if (!m.available || !variants.length) {
          price.textContent = 'Sold out';
          var view = document.createElement('a');
          view.className = 'button button--secondary button--full-width';
          view.href = m.url;
          view.textContent = 'View details';
          actions.appendChild(view);
          return li;
        }

        price.textContent = variants.length > 1 ? 'From ' + this.lowest(variants) : variants[0].price;
        var select = null;
        if (variants.length > 1) {
          var selId = 'nse-fmm-v-' + m.id;
          var label = document.createElement('label');
          label.className = 'nse-fmm__label';
          label.setAttribute('for', selId);
          label.textContent = 'Option';
          select = document.createElement('select');
          select.id = selId;
          select.className = 'select__select nse-fmm__select';
          variants.forEach((v) => {
            var o = document.createElement('option');
            o.value = v.id;
            o.textContent = v.title + ' - ' + v.price;
            select.appendChild(o);
          });
          select.addEventListener('change', () => {
            var v = variants.find((x) => String(x.id) === select.value);
            if (v) price.textContent = v.price;
          });
          actions.appendChild(label);
          actions.appendChild(select);
        }
        var add = document.createElement('button');
        add.type = 'button';
        add.className = 'button button--primary button--full-width nse-fmm__add';
        add.textContent = 'Add to cart';
        add.setAttribute('aria-label', 'Add to cart: ' + m.title);
        var msg = document.createElement('p');
        msg.className = 'nse-fmm__msg';
        msg.setAttribute('role', 'status');
        add.addEventListener('click', () => this.addToCart(m, select ? select.value : variants[0].id, add, msg));
        actions.appendChild(add);
        actions.appendChild(msg);
        var details = document.createElement('a');
        details.className = 'link nse-fmm__details';
        details.href = m.url;
        details.textContent = 'View details';
        actions.appendChild(details);
        return li;
      }

      lowest(variants) {
        // Prices arrive pre-formatted; variants come in catalog order, so pick the first with the lowest number.
        var best = variants[0];
        var num = (v) => parseFloat(String(v.price).replace(/[^0-9.]/g, '')) || 0;
        variants.forEach((v) => {
          if (num(v) < num(best)) best = v;
        });
        return best.price;
      }

      addToCart(m, variantId, button, msg) {
        // Hard guard: quote-only machines are never added to the cart.
        if (!m || m.quote) return;
        button.setAttribute('aria-disabled', 'true');
        button.disabled = true;
        msg.textContent = 'Adding...';
        var url = (this.dataset.cartAddUrl || '/cart/add') + '.js';
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: 'cart-icon-bubble' }),
        })
          .then((r) => r.json().then((j) => ({ ok: r.ok, j: j })))
          .then((res) => {
            if (!res.ok) throw new Error((res.j && (res.j.description || res.j.message)) || 'Could not add');
            msg.innerHTML = '';
            msg.append('Added to cart. ');
            var a = document.createElement('a');
            a.href = this.dataset.cartUrl || '/cart';
            a.className = 'link';
            a.textContent = 'View cart';
            msg.appendChild(a);
            var bubble = document.getElementById('cart-icon-bubble');
            var html = res.j && res.j.sections && res.j.sections['cart-icon-bubble'];
            if (bubble && html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var fresh = doc.querySelector('.shopify-section');
              if (fresh) bubble.innerHTML = fresh.innerHTML;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nse-find-my-machine', productVariantId: variantId });
            }
          })
          .catch((e) => {
            msg.textContent = 'Sorry, that could not be added: ' + e.message;
          })
          .finally(() => {
            button.disabled = false;
            button.removeAttribute('aria-disabled');
          });
      }
    }
  );
}
